import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, GraduationCap } from 'lucide-react';
import { COACHES } from '../lib/coaches';
import { FeaturedEventCard } from '../components/FeaturedEventCard';
import { Footer } from '../components/Footer';
import { Hero } from '../components/sections/Hero';
import { JuniorTrialForm } from '../components/JuniorTrialForm';
import { Navigation } from '../components/Navigation';
import { ORGANIZATION_LD } from '../components/SEO';
import { SEO } from '../components/SEO';
import { SITE_URL } from '../lib/constants';
import { WhatsAppButton, WhatsAppIcon } from '../components/WhatsAppButton';
import { useLanguage } from '../context/LanguageContext';

const AcademyPage = () => {
  const { t } = useLanguage();

  const trainingPackages = [
    {
      title: t.academyPage.pkg1Title,
      description: t.academyPage.pkg1Desc,
      price: "120",
      duration: t.academyPage.pkg1Duration,
      features: [t.academyPage.pkg1Feat1, t.academyPage.pkg1Feat2, t.academyPage.pkg1Feat3],
      priceNote: t.academyPage.pkg1PriceNote
    },
    {
      title: t.academyPage.pkg2Title,
      description: t.academyPage.pkg2Desc,
      price: "55",
      duration: t.academyPage.pkg2Duration,
      features: [t.academyPage.pkg2Feat1, t.academyPage.pkg2Feat2, t.academyPage.pkg2Feat3],
      popular: true
    },
    {
      title: t.academyPage.pkg3Title,
      description: t.academyPage.pkg3Desc,
      price: "399",
      duration: t.academyPage.pkg3Duration,
      features: [t.academyPage.pkg3Feat1, t.academyPage.pkg3Feat2, t.academyPage.pkg3Feat3],
      priceNote: t.academyPage.pkg3PriceNote
    }
  ];

  const ageGroups = [
    { name: t.academyPage.age1Name, age: t.academyPage.age1Range, level: t.academyPage.age1Level },
    { name: t.academyPage.age2Name, age: t.academyPage.age2Range, level: t.academyPage.age2Level },
    { name: t.academyPage.age3Name, age: t.academyPage.age3Range, level: t.academyPage.age3Level },
    { name: t.academyPage.age4Name, age: t.academyPage.age4Range, level: t.academyPage.age4Level }
  ];

  const coaches = COACHES.map(c => ({
    slug: c.slug,
    name: c.name,
    image: c.image,
    title: t.trainers[c.titleKey],
    experience: t.trainers[c.experienceKey],
    specialty: t.trainers[c.specialtyKey]
  }));

  const seo = t.seo.academy;
  const academyHeroImg = 'https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/nj8h18ke_DSC06837.webp';
  const academyJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'EducationalOrganization',
      '@id': `${SITE_URL}/academy#academy`,
      name: 'Padelarena Junior Academy Wädenswil',
      alternateName: 'Padel Junior Academy Schweiz',
      url: `${SITE_URL}/academy`,
      image: academyHeroImg,
      description: seo.description,
      address: ORGANIZATION_LD.address,
      telephone: '+41 43 833 0834',
      areaServed: ORGANIZATION_LD.areaServed,
      parentOrganization: { '@id': `${SITE_URL}/#organization` },
      keywords: seo.keywords,
      audience: [
        { '@type': 'PeopleAudience', audienceType: 'Children', suggestedMinAge: 4, suggestedMaxAge: 17 },
        { '@type': 'PeopleAudience', audienceType: 'Families with kids' }
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Junior padel programmes',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Course', name: t.academyPage.age1Name, description: `${t.academyPage.age1Range} – ${t.academyPage.age1Level}`, provider: { '@id': `${SITE_URL}/#organization` } } },
          { '@type': 'Offer', itemOffered: { '@type': 'Course', name: t.academyPage.age2Name, description: `${t.academyPage.age2Range} – ${t.academyPage.age2Level}`, provider: { '@id': `${SITE_URL}/#organization` } } },
          { '@type': 'Offer', itemOffered: { '@type': 'Course', name: t.academyPage.age3Name, description: `${t.academyPage.age3Range} – ${t.academyPage.age3Level}`, provider: { '@id': `${SITE_URL}/#organization` } } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Free trial lesson', description: t.academyPage.juniorTrial.subtitle } }
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <SEO path="/academy" title={seo.title} description={seo.description} keywords={seo.keywords} image={academyHeroImg} jsonLd={academyJsonLd} />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-40 pb-24 px-6 md:px-12 dark-grid-section">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="energy-bar w-24 mb-6 mx-auto"></div>
            <p className="overline mb-4">{t.academyPage.label}</p>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black mb-8 text-white uppercase"
          >
            <span className="gradient-text">{t.academyPage.heroTitleStart}</span> {t.academyPage.heroTitleEnd}
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto"
          >
            {t.academyPage.heroSubtitle}
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-wrap gap-3 justify-center"
          >
            <a
              href="#junior-trial"
              data-testid="academy-hero-trial-btn"
              className="athletic-button athletic-button-primary inline-flex items-center gap-2"
            >
              <GraduationCap className="w-5 h-5" />
              {t.academyPage.juniorTrial.scrollCta}
            </a>
            <WhatsAppButton
              linkKey="booking"
              className="athletic-button athletic-button-secondary inline-flex items-center gap-2"
              testId="academy-hero-whatsapp-btn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {t.academyPage.bookViaWhatsapp}
            </WhatsAppButton>
          </motion.div>
        </div>
      </section>

      {/* Training Packages */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="energy-bar w-24 mb-6 mx-auto"></div>
            <p className="overline mb-4">{t.academyPage.packagesLabel}</p>
            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight uppercase">
              {t.academyPage.packagesTitleStart} <span className="gradient-text">{t.academyPage.packagesTitleAccent}</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t.academyPage.packagesSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {trainingPackages.map((pkg, index) => (
              <div
                key={index}
                className={`relative rounded-3xl p-8 transition-all duration-300 ${
                  pkg.popular
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-2xl scale-105'
                    : 'bg-white border-2 border-slate-200 hover:border-blue-500 hover:shadow-xl'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-sky-400 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase">
                      {t.academyPage.popular}
                    </span>
                  </div>
                )}
                
                <h3 className={`text-2xl font-bold mb-3 ${pkg.popular ? 'text-white' : 'text-slate-900'}`}>
                  {pkg.title}
                </h3>
                <p className={`text-sm mb-6 ${pkg.popular ? 'text-blue-100' : 'text-slate-600'}`}>
                  {pkg.description}
                </p>
                
                <div className="mb-6">
                  {pkg.priceNote && (
                    <span className={`text-sm uppercase tracking-wider font-semibold ${pkg.popular ? 'text-blue-200' : 'text-slate-500'}`}>
                      {pkg.priceNote}{' '}
                    </span>
                  )}
                  <span className={`text-5xl font-black ${pkg.popular ? 'text-white' : 'text-blue-600'}`}>
                    {pkg.price}
                  </span>
                  <span className={`text-xl ml-2 ${pkg.popular ? 'text-blue-100' : 'text-slate-600'}`}>
                    CHF
                  </span>
                  <p className={`text-sm mt-1 ${pkg.popular ? 'text-blue-100' : 'text-slate-500'}`}>
                    {pkg.duration}
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className={`w-5 h-5 ${pkg.popular ? 'text-blue-200' : 'text-green-500'}`} />
                      <span className={`text-sm ${pkg.popular ? 'text-white' : 'text-slate-700'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <WhatsAppButton
                  linkKey="booking"
                  testId={`academy-pkg-book-${pkg.title}`}
                  className={`block w-full text-center px-6 py-3 rounded-xl font-bold transition-all ${
                    pkg.popular
                      ? 'bg-white text-blue-600 hover:bg-blue-50'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {t.academyPage.bookNow}
                </WhatsAppButton>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Adult Academy */}
      <section className="py-24 bg-white" data-testid="adult-academy-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <div className="energy-bar w-24 mb-6 mx-auto"></div>
            <p className="overline mb-4">{t.academyPage.adultAcademy.sectionLabel}</p>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight uppercase">
              {t.academyPage.adultAcademy.sectionTitleStart}{' '}
              <span className="gradient-text">{t.academyPage.adultAcademy.sectionTitleAccent}</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t.academyPage.adultAcademy.sectionSubtitle}
            </p>
            <div className="inline-flex items-center gap-2 mt-5 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
              {t.academyPage.adultAcademy.programBadge}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8">
            {/* Before 17:00 */}
            <div className="relative rounded-3xl p-8 bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors" data-testid="adult-academy-before">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">{t.academyPage.adultAcademy.beforeLabel}</p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl md:text-6xl font-black text-slate-900">{t.academyPage.adultAcademy.beforePrice}</span>
                <span className="text-slate-500 text-sm">{t.academyPage.adultAcademy.beforeNote}</span>
              </div>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2.5 text-slate-700 text-sm">
                  <Check className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                  <span>{t.academyPage.adultAcademy.feat1}</span>
                </li>
                <li className="flex items-start gap-2.5 text-slate-700 text-sm">
                  <Check className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                  <span>{t.academyPage.adultAcademy.feat2}</span>
                </li>
                <li className="flex items-start gap-2.5 text-slate-700 text-sm">
                  <Check className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                  <span>{t.academyPage.adultAcademy.feat3}</span>
                </li>
              </ul>
            </div>

            {/* After 17:00 */}
            <div className="relative rounded-3xl p-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-2xl" data-testid="adult-academy-after">
              <p className="text-xs font-bold uppercase tracking-widest text-sky-300 mb-3">{t.academyPage.adultAcademy.afterLabel}</p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl md:text-6xl font-black text-white">{t.academyPage.adultAcademy.afterPrice}</span>
                <span className="text-slate-400 text-sm">{t.academyPage.adultAcademy.afterNote}</span>
              </div>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2.5 text-slate-200 text-sm">
                  <Check className="w-4 h-4 mt-0.5 text-sky-400 flex-shrink-0" />
                  <span>{t.academyPage.adultAcademy.feat1}</span>
                </li>
                <li className="flex items-start gap-2.5 text-slate-200 text-sm">
                  <Check className="w-4 h-4 mt-0.5 text-sky-400 flex-shrink-0" />
                  <span>{t.academyPage.adultAcademy.feat2}</span>
                </li>
                <li className="flex items-start gap-2.5 text-slate-200 text-sm">
                  <Check className="w-4 h-4 mt-0.5 text-sky-400 flex-shrink-0" />
                  <span>{t.academyPage.adultAcademy.feat3}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center">
            <p className="text-slate-500 text-sm mb-5" data-testid="adult-academy-group-note">
              {t.academyPage.adultAcademy.groupNote}
            </p>
            <WhatsAppButton
              linkKey="booking"
              testId="adult-academy-cta-btn"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-bold bg-[#5B7FA8] hover:bg-[#4A6D95] text-white transition-colors shadow-lg shadow-[#5B7FA8]/30"
            >
              {t.academyPage.adultAcademy.ctaButton}
            </WhatsAppButton>
          </div>
        </div>
      </section>

      {/* Junior Camp spotlight — surfaces the current featured kids event */}
      <section className="py-16 bg-slate-50" data-testid="junior-camp-highlight-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <FeaturedEventCard eventKey="juniorCamp" testId="featured-event-juniorcamp-academy" />
        </div>
      </section>

      {/* Junior Academy Memberships / Abos */}
      <section className="py-24 dark-section" data-testid="junior-abos-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="energy-bar w-24 mb-6 mx-auto"></div>
            <p className="overline mb-4">{t.academyPage.juniorAbos.sectionLabel}</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight uppercase">
              {t.academyPage.juniorAbos.sectionTitleStart}{' '}
              <span className="gradient-text">{t.academyPage.juniorAbos.sectionTitleAccent}</span>
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              {t.academyPage.juniorAbos.sectionSubtitle}
            </p>
            <div className="inline-flex items-center gap-2 mt-5 px-4 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300 text-xs font-bold uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse"></span>
              {t.academyPage.juniorAbos.season}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {/* 3-month pass */}
            <div className="relative rounded-3xl p-8 bg-white/5 border border-white/10 hover:border-sky-400/40 transition-colors" data-testid="junior-abo-3month">
              <p className="text-xs font-bold uppercase tracking-widest text-sky-400 mb-3">{t.academyPage.juniorAbos.threeMonthLabel}</p>
              <h3 className="text-2xl font-bold text-white mb-2">{t.academyPage.juniorAbos.threeMonthTitle}</h3>
              <p className="text-slate-400 mb-6 text-sm">{t.academyPage.juniorAbos.threeMonthDesc}</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-5 py-4">
                  <span className="text-white text-sm md:text-base font-medium">{t.academyPage.juniorAbos.threeMonthOption1}</span>
                  <span className="text-2xl font-black text-sky-400">{t.academyPage.juniorAbos.threeMonthPrice1}</span>
                </div>
              </div>
            </div>

            {/* Yearly pass — Recreational (highlight) */}
            <div className="relative rounded-3xl p-8 bg-gradient-to-br from-sky-500 to-blue-700 text-white shadow-2xl" data-testid="junior-abo-yearly">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-white text-blue-700 px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">
                  {t.academyPage.juniorAbos.popular}
                </span>
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-sky-100 mb-3">{t.academyPage.juniorAbos.yearlyLabel}</p>
              <h3 className="text-2xl font-bold mb-2">{t.academyPage.juniorAbos.yearlyTitle}</h3>
              <p className="text-blue-100 mb-6 text-sm">{t.academyPage.juniorAbos.yearlyDesc}</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white/15 border border-white/20 rounded-xl px-5 py-4">
                  <span className="text-white text-sm md:text-base font-medium">{t.academyPage.juniorAbos.yearlyOption1}</span>
                  <span className="text-2xl font-black text-white">{t.academyPage.juniorAbos.yearlyPrice1}</span>
                </div>
                <div className="flex items-center justify-between bg-white/15 border border-white/20 rounded-xl px-5 py-4">
                  <span className="text-white text-sm md:text-base font-medium">{t.academyPage.juniorAbos.yearlyOption2}</span>
                  <span className="text-2xl font-black text-white">{t.academyPage.juniorAbos.yearlyPrice2}</span>
                </div>
              </div>
            </div>

            {/* Competitive Players */}
            <div className="relative rounded-3xl p-8 bg-white/5 border border-white/10 hover:border-sky-400/40 transition-colors flex flex-col" data-testid="junior-abo-competitive">
              <p className="text-xs font-bold uppercase tracking-widest text-sky-400 mb-3">{t.academyPage.juniorAbos.competitiveLabel}</p>
              <h3 className="text-2xl font-bold text-white mb-2">{t.academyPage.juniorAbos.competitiveTitle}</h3>
              <p className="text-slate-400 mb-6 text-sm">{t.academyPage.juniorAbos.competitiveDesc}</p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2.5 text-slate-200 text-sm">
                  <Check className="w-4 h-4 mt-0.5 text-sky-400 flex-shrink-0" />
                  <span>{t.academyPage.juniorAbos.competitiveOption1}</span>
                </div>
                <div className="flex items-start gap-2.5 text-slate-200 text-sm">
                  <Check className="w-4 h-4 mt-0.5 text-sky-400 flex-shrink-0" />
                  <span>{t.academyPage.juniorAbos.competitiveOption2}</span>
                </div>
              </div>
              <div className="mt-auto">
                <div className="flex items-baseline justify-between bg-white/5 border border-white/10 rounded-xl px-5 py-4 mb-4">
                  <span className="text-slate-300 text-xs uppercase tracking-widest">Price</span>
                  <span className="text-xl font-bold text-sky-400">{t.academyPage.juniorAbos.competitivePrice}</span>
                </div>
                <WhatsAppButton
                  linkKey="booking"
                  testId="junior-abo-competitive-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold bg-[#5B7FA8] hover:bg-[#4A6D95] text-white transition-colors"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                  {t.academyPage.juniorAbos.competitiveCta}
                </WhatsAppButton>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-300 text-lg mb-2 font-semibold">{t.academyPage.juniorAbos.ctaTitle}</p>
            <p className="text-slate-400 max-w-xl mx-auto mb-6 text-sm md:text-base">{t.academyPage.juniorAbos.ctaSubtitle}</p>
            <WhatsAppButton
              linkKey="booking"
              testId="junior-abos-cta-btn"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-bold bg-[#5B7FA8] hover:bg-[#4A6D95] text-white transition-colors shadow-lg shadow-[#5B7FA8]/30"
            >
              <WhatsAppIcon className="w-5 h-5" />
              {t.academyPage.juniorAbos.ctaButton}
            </WhatsAppButton>
          </div>
        </div>
      </section>

      {/* Age Groups */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="energy-bar w-24 mb-6 mx-auto"></div>
            <p className="overline mb-4">{t.academyPage.ageGroupsLabel}</p>
            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight uppercase">
              {t.academyPage.ageGroupsTitleStart} <span className="gradient-text">{t.academyPage.ageGroupsTitleAccent}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ageGroups.map((group, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 border-2 border-slate-200 hover:border-blue-500 transition-all hover:shadow-lg">
                <h3 className="text-xl font-bold text-slate-900 mb-3">{group.name}</h3>
                <p className="text-blue-600 font-semibold mb-2">{group.age}</p>
                <p className="text-sm text-slate-600">{group.level}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coaches */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="energy-bar w-24 mb-6 mx-auto"></div>
            <p className="overline mb-4">{t.trainers.meet.toUpperCase()}</p>
            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 tracking-tight uppercase">
              {t.academyPage.coachesTitleStart} <span className="gradient-text">{t.academyPage.coachesTitleAccent}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coaches.map((coach, index) => (
              <Link
                key={index}
                to={`/coach/${coach.slug}`}
                data-testid={`academy-coach-card-${coach.slug}`}
                className="block rounded-3xl overflow-hidden border-2 border-slate-200 hover:border-blue-500 transition-all hover:shadow-2xl group"
              >
                <div className="relative h-96 overflow-hidden">
                  <img 
                    src={coach.image}
                    alt={coach.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                  <div className="absolute top-4 right-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-[10px] font-bold uppercase tracking-wider text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity">
                    {t.trainers.viewBio} →
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">{coach.name}</h3>
                    <p className="text-blue-300 font-semibold text-sm mb-2">{coach.title}</p>
                  </div>
                </div>
                <div className="p-6 bg-white">
                  <div className="flex justify-between items-center gap-3 mb-2 pb-2 border-b border-slate-100">
                    <span className="text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">{t.trainers.experience}</span>
                    <span className="text-sm font-bold text-slate-900 text-right">{coach.experience}</span>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">{t.trainers.specialty}</span>
                    <span className="text-sm font-bold text-slate-900 text-right">{coach.specialty}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Junior Trial Lesson Form */}
      <section id="junior-trial" className="py-24 dark-grid-section scroll-mt-24" data-testid="junior-trial-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-12">
            <div className="energy-bar w-24 mb-6 mx-auto"></div>
            <p className="overline mb-4">{t.academyPage.juniorTrial.label}</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight uppercase">
              {t.academyPage.juniorTrial.title} <span className="gradient-text">{t.academyPage.juniorTrial.titleAccent}</span>
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-3">
              {t.academyPage.juniorTrial.subtitle}
            </p>
            <p className="inline-block px-4 py-1.5 rounded-full bg-[#5B7FA8]/15 border border-[#5B7FA8]/30 text-sm font-semibold text-[#38BDF8]">
              {t.academyPage.juniorTrial.juniorsOnly}
            </p>
          </div>

          <JuniorTrialForm />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 dark-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 uppercase">
            {t.academyPage.ctaTitle}
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            {t.academyPage.ctaSubtitle}
          </p>
          <WhatsAppButton
            linkKey="booking"
            className="athletic-button athletic-button-secondary inline-flex items-center gap-2 text-lg"
            testId="academy-cta-whatsapp-btn"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {t.academyPage.bookViaWhatsapp}
          </WhatsAppButton>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Coach Bio Page

export default AcademyPage;
